import { createRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '@arabquiz/ui'
import {
  ArrowLeft,
  Languages,
  LayoutDashboard,
  LogIn,
  LogOut,
  PenSquare,
  Sparkles,
  Users,
  Zap,
} from 'lucide-react'
import { Route as rootRoute } from './__root'

/**
 * Public marketing / landing page at `/`.
 *
 * Visual direction: editorial dark luxe. Deep brand-blue gradient backdrop
 * with the same atmospheric blurred orbs used across the host and player
 * surfaces so the brand reads as one cohesive surface from first paint to
 * the in-game projector. Tajawal display headings carry the typographic
 * weight; body copy stays on IBM Plex Sans Arabic for readability.
 *
 * The page serves two distinct audiences from one screen:
 *   - Players land here from a QR code or word-of-mouth and want exactly
 *     one thing: an obvious path to "انضم للعبة" → `/play`.
 *   - Hosts arrive with the intent to author or run a quiz and want a
 *     login affordance: "إنشاء اختبار" → `/auth/sign-in`.
 *
 * The hero foregrounds both actions with parity (the player CTA is the
 * primary white-on-dark button because the player journey is the more
 * frequent first-time interaction; the host CTA is the secondary
 * outlined button so an authenticated host still sees an obvious path).
 *
 * Below the hero, three feature cards summarise the value proposition.
 * Each carries a coloured icon halo drawn from the in-game answer tile
 * palette (red / blue / amber / emerald) so first-time visitors get a
 * subliminal preview of the visual language they'll see during a game.
 *
 * Layout rules:
 *   - All single-sided spacing uses logical utilities (`ps-*`, `pe-*`,
 *     `start-*`, `end-*`) so the page mirrors cleanly under `dir="rtl"`.
 *   - Symmetric `px-*` / `py-*` is fine and matches the surrounding
 *     code style.
 *   - Animations are CSS-only (transition on hover/active scale) to keep
 *     the bundle lean and to avoid race conditions with `font-display`
 *     swap-in on first paint.
 */
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
})

type MeResponse = {
  userId: string
  email: string
  displayName: string
  emailConfirmed: boolean
  organizationId: string
  locale: string
}

function HomePage() {
  // Resolve identity so this public page can reflect auth state without
  // living under the `_app` gate. We deliberately reuse the SAME
  // `['auth', 'me']` query key as `_app.tsx`, so once an authenticated
  // visitor has loaded the app this read is served from cache with no
  // extra round-trip. Unlike `_app.tsx` we treat 401 as a value (return
  // `null`) rather than throwing: this page is happy to render for an
  // anonymous viewer, so there is no redirect to schedule and no reason
  // to retry the request.
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/auth/me', {
        credentials: 'include',
        signal,
      })
      if (res.status === 401) return null
      if (!res.ok) return null
      return (await res.json()) as MeResponse
    },
    retry: false,
    staleTime: 60_000,
  })

  const isAuthed = !!meQuery.data
  const isResolving = meQuery.isLoading

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Sign the host out from the public landing page. On success we drop the
  // shared `['auth', 'me']` cache so the TopBar flips back to its anonymous
  // state, then route to `/` (the gate would do the same anyway).
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await fetch('/api/v1/auth/sign-out', {
        method: 'POST',
        credentials: 'include',
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      void navigate({ to: '/' })
    },
  })

  return (
    <main className="relative min-h-screen overflow-hidden bg-gradient-to-bl from-brand-900 via-brand-800 to-brand-900 text-white">
      {/* Ambient atmosphere — soft top radial highlight plus two off-canvas
          blurred orbs so the dark backdrop never reads as flat. Mirrors
          the chrome used by the host control panel and the /play join
          form so the brand surface feels continuous across the app. */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.18),transparent_60%)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -top-32 -end-32 h-96 w-96 rounded-full bg-brand-400/25 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-32 -start-32 h-[28rem] w-[28rem] rounded-full bg-brand-300/15 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <TopBar
          isAuthed={isAuthed}
          isResolving={isResolving}
          onLogout={() => logoutMutation.mutate()}
          isLoggingOut={logoutMutation.isPending}
        />

        <Hero isAuthed={isAuthed} />

        <FeatureGrid />

        <SiteFooter />
      </div>
    </main>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Top bar — brand mark + minimal nav
// ─────────────────────────────────────────────────────────────────────────────

type TopBarProps = {
  isAuthed: boolean
  isResolving: boolean
  onLogout: () => void
  isLoggingOut: boolean
}

function TopBar({ isAuthed, isResolving, onLogout, isLoggingOut }: TopBarProps) {
  const { t: tCommon } = useTranslation('common')
  return (
    <header className="flex items-center justify-between">
      <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 ps-4 pe-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-100 backdrop-blur-sm">
        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        <span>ArabQuiz</span>
      </span>

      {/* Until auth resolves we render nothing in this slot: showing the
          login link first would flash "تسجيل الدخول" to a logged-in
          visitor before swapping it for the dashboard link. Resolution
          is sub-second (and usually instant from shared cache). */}
      {isResolving ? null : isAuthed ? (
        <div className="flex items-center gap-2">
          <a
            href="/dashboard"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 ps-4 pe-4 py-1.5 text-xs font-semibold text-brand-100 backdrop-blur-sm transition-colors hover:bg-white/10 hover:text-white"
          >
            <LayoutDashboard className="h-3.5 w-3.5" aria-hidden="true" />
            <span>لوحة التحكم</span>
          </a>
          <button
            type="button"
            onClick={onLogout}
            disabled={isLoggingOut}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 ps-4 pe-4 py-1.5 text-xs font-semibold text-brand-100 backdrop-blur-sm transition-colors hover:bg-white/10 hover:text-white disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{tCommon('logout')}</span>
          </button>
        </div>
      ) : (
        <a
          href="/auth/sign-in"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 ps-4 pe-4 py-1.5 text-xs font-semibold text-brand-100 backdrop-blur-sm transition-colors hover:bg-white/10 hover:text-white"
        >
          <LogIn className="h-3.5 w-3.5" aria-hidden="true" />
          <span>تسجيل الدخول</span>
        </a>
      )}
    </header>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────────────────

type HeroProps = {
  isAuthed: boolean
}

function Hero({ isAuthed }: HeroProps) {
  // Logged-in hosts go straight to their quizzes list; anonymous visitors
  // (and the brief pre-resolution window, where `isAuthed` is still false)
  // go to sign-in — the `_app` gate would route them there anyway.
  const createHref = isAuthed ? '/quizzes' : '/auth/sign-in'
  return (
    <section className="flex flex-1 flex-col items-center justify-center text-center py-14 sm:py-20">
      <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 ps-4 pe-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-100 backdrop-blur-sm">
        <Sparkles className="h-3.5 w-3.5 text-amber-200" aria-hidden="true" />
        <span>منصة اختبارات تفاعلية</span>
      </span>

      {/* Brand wordmark. Kept in the Latin alphabet because the product
          name is "ArabQuiz" everywhere (URLs, social, app icon) — using
          the Tajawal display face still ties it to the Arabic body. */}
      <h1
        className="mt-6 font-display text-6xl sm:text-7xl lg:text-8xl font-bold tracking-tight text-white drop-shadow-[0_8px_30px_rgba(0,0,0,0.45)]"
        dir="ltr"
      >
        ArabQuiz
      </h1>

      <p className="mt-5 font-display text-2xl sm:text-3xl font-bold tracking-tight text-brand-100">
        منصة اختبارات تفاعلية عربية
      </p>

      <p className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-brand-100/80">
        أنشئ اختبارات حية، شارك الرمز مع المشاركين، وتابع النتائج لحظة بلحظة —
        كل ذلك بواجهة عربية أنيقة وسريعة على كل جهاز.
      </p>

      <div className="mt-10 flex w-full max-w-md flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
        <a href="/play" className="block w-full sm:w-auto">
          <Button
            type="button"
            size="lg"
            className="h-14 w-full sm:w-auto gap-2 bg-white text-brand-900 text-lg font-bold shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.02] active:scale-100 ps-7 pe-7"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            <span>انضم للعبة</span>
          </Button>
        </a>

        <a href={createHref} className="block w-full sm:w-auto">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="h-14 w-full sm:w-auto gap-2 border-white/25 bg-white/5 text-white text-lg font-bold backdrop-blur-sm transition-colors hover:bg-white/10 hover:text-white hover:border-white/40 ps-7 pe-7"
          >
            <PenSquare className="h-5 w-5" aria-hidden="true" />
            <span>إنشاء اختبار</span>
          </Button>
        </a>
      </div>

      {/* Brand signature: the four answer-tile colours from the in-game
          MCQ grid. Tiny, decorative, but instantly recognisable once a
          visitor has played a round — and an interesting accent on
          first paint. */}
      <PaletteStripe />
    </section>
  )
}

function PaletteStripe() {
  // Same palette as the player MCQ tiles (red / blue / yellow / green)
  // — see `PLAYER_TILE_COLORS` in PlayerGameView. We keep the order
  // identical so the visual handshake holds when a visitor lands on
  // /play and sees the same colours laid out as tiles.
  const tones = [
    'bg-red-500',
    'bg-blue-500',
    'bg-yellow-500',
    'bg-green-500',
  ] as const
  return (
    <div
      className="mt-12 flex items-center gap-2"
      role="presentation"
      aria-hidden="true"
    >
      {tones.map((tone) => (
        <span
          key={tone}
          className={`h-1.5 w-10 sm:w-14 rounded-full ${tone} shadow-md`}
        />
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Feature highlights
// ─────────────────────────────────────────────────────────────────────────────

function FeatureGrid() {
  return (
    <section
      aria-labelledby="features-heading"
      className="mt-10 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6"
    >
      <h2 id="features-heading" className="sr-only">
        مميزات المنصة
      </h2>

      <FeatureCard
        icon={<Zap className="h-5 w-5" aria-hidden="true" />}
        iconBg="bg-red-500/20"
        iconRing="ring-red-300/40"
        iconColor="text-red-200"
        title="سهل الاستخدام"
        description="انضم برمز من ست خانات في ثوانٍ — لا حاجة لتسجيل حساب للمشاركين، ولا تنزيل لأي تطبيق."
      />

      <FeatureCard
        icon={<Users className="h-5 w-5" aria-hidden="true" />}
        iconBg="bg-blue-500/20"
        iconRing="ring-blue-300/40"
        iconColor="text-blue-200"
        title="تفاعلي"
        description="نتائج لحظية بعد كل سؤال، لوحة متصدّرين حيّة، وتجربة لعب متزامنة لعشرات المشاركين."
      />

      <FeatureCard
        icon={<Languages className="h-5 w-5" aria-hidden="true" />}
        iconBg="bg-emerald-500/20"
        iconRing="ring-emerald-300/40"
        iconColor="text-emerald-200"
        title="عربي أولاً"
        description="واجهة من اليمين إلى اليسار من البداية، خطوط عربية مختارة بعناية، ودعم كامل للصيغ الرياضية."
      />
    </section>
  )
}

type FeatureCardProps = {
  icon: React.ReactNode
  iconBg: string
  iconRing: string
  iconColor: string
  title: string
  description: string
}

function FeatureCard({
  icon,
  iconBg,
  iconRing,
  iconColor,
  title,
  description,
}: FeatureCardProps) {
  return (
    <article className="group rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl ps-6 pe-6 py-7 transition-all duration-200 hover:bg-white/[0.08] hover:border-white/20 hover:-translate-y-0.5">
      <span
        className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${iconBg} ring-1 ${iconRing} ${iconColor}`}
        aria-hidden="true"
      >
        {icon}
      </span>

      <h3 className="mt-5 font-display text-xl sm:text-2xl font-bold tracking-tight text-white">
        {title}
      </h3>

      <p className="mt-2 text-sm sm:text-base leading-relaxed text-brand-100/75">
        {description}
      </p>
    </article>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────────────────────

function SiteFooter() {
  return (
    <footer className="mt-12 sm:mt-16 border-t border-white/10 pt-6 sm:pt-8 text-center">
      <p className="text-xs text-brand-100/60 leading-relaxed">
        <span className="font-display font-bold tracking-wide text-brand-100/80">
          ArabQuiz
        </span>
        <span className="mx-2 text-brand-100/30" aria-hidden="true">·</span>
        <span>صُمم للجمهور العربي</span>
      </p>
    </footer>
  )
}
